import { Button } from '@hologram-dimension/button';
import { Panel } from '@hologram-dimension/panel';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import useDeviceWebhook from 'api/legacy/devices/useDeviceWebhook';
import useRouterCredentials from 'api/legacy/devices/useRouterCredentials';
import useSimDeviceDetails from 'api/sims/useSimDeviceDetails';
import Field from 'components/Field/Field';
import WebhookPanel from 'components/WebhookPanel/WebhookPanel';
import RouterCredentialsModal from 'modals/RouterCredsModal/RouterCredsModal';
import { useState, type FC } from 'react';
import useOrganization from 'state/contexts/Organization/useOrganization';

interface WebhookProps {
  hologramSimId?: HologramSimId;
}

const Webhooks: FC<WebhookProps> = ({ hologramSimId }) => {
  const [routerCredsModalOpen, setRouterCredsModalOpen] = useState(false);

  const organizationId = useOrganization();
  const {
    isLoading: isDeviceLoading,
    error: deviceError,
    deviceDetails,
  } = useSimDeviceDetails(hologramSimId);
  const deviceId = deviceDetails?.claimedDeviceId;
  const { isLoading: deviceDetailsIsLoading } = useDeviceWebhook(deviceId, organizationId);

  const { isLoading: isLoadingRouterCredentials, error: routerCredentialsError } =
    useRouterCredentials({
      organizationId,
      deviceId,
    });

  const isLoading = isDeviceLoading || deviceDetailsIsLoading || isLoadingRouterCredentials;

  const error = deviceError || routerCredentialsError;

  if (error) {
    return (
      <PanelNotification
        variant="error"
        label="We ran into an issue loading Webhook data"
        actions={
          <Button
            variant="secondary"
            iconStart="Sync"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload page
          </Button>
        }
      >
        We will keep trying in the background or you can try to reload the page.
      </PanelNotification>
    );
  }

  return (
    <>
      <WebhookPanel deviceId={deviceId} />
      <Panel header="Data engine" isLoading={isLoading}>
        <Field
          label="Device key"
          value={
            <Button
              onClick={() => {
                setRouterCredsModalOpen(true);
              }}
              variant="secondary"
            >
              Show device key
            </Button>
          }
        />
      </Panel>
      {routerCredsModalOpen && (
        <RouterCredentialsModal
          deviceId={deviceId}
          onClose={() => {
            setRouterCredsModalOpen(false);
          }}
          defaultOpen
        />
      )}
    </>
  );
};

export default Webhooks;
